/**
 * @zendir/ui - SidePanel Component
 *
 * A sliding panel that pushes content instead of overlaying it.
 * Designed to be placed as a flex sibling so the parent layout
 * naturally adjusts when the panel opens/closes.
 *
 * Features:
 * - Push-content behavior (flex sibling, content adjusts automatically)
 * - Smooth slide-in/out animation
 * - Configurable width, position (left/right)
 * - Optional overlay backdrop (for modal-like behavior)
 * - Theme-integrated via useTheme()
 * - Accessible (focus trap, keyboard support)
 * - Close on escape key
 *
 * @example
 * ```tsx
 * // Push-content pattern: SidePanel as flex sibling
 * <div style={{ display: 'flex', height: '100vh' }}>
 *   <main style={{ flex: 1, transition: 'all 0.3s ease' }}>
 *     {children}
 *   </main>
 *   <SidePanel
 *     open={isChatOpen}
 *     onClose={() => setIsChatOpen(false)}
 *     title="Chat Assistant"
 *     width={380}
 *   >
 *     <ChatPanel messages={messages} onSend={handleSend} />
 *   </SidePanel>
 * </div>
 * ```
 */

import React, { memo, useEffect, useRef, useCallback, useState } from "react";
import { useTheme } from "../../theme";
import { classNames } from "../../utils";
import { useBreakpoint } from "../layout/useBreakpoint";

// ============================================================================
// Types
// ============================================================================

export type SidePanelPosition = "left" | "right";

export interface SidePanelProps {
  /** Whether the panel is open */
  open: boolean;
  /** Called when the panel should close */
  onClose: () => void;
  /** Panel title displayed in the header */
  title?: string;
  /** Panel width in px or CSS string (default: 380) */
  width?: number | string;
  /** Which side the panel slides in from (default: 'right') */
  position?: SidePanelPosition;
  /** Show a close button in the header (default: true) */
  showCloseButton?: boolean;
  /** Close when pressing Escape (default: true) */
  closeOnEscape?: boolean;
  /** Show a semi-transparent overlay behind the panel (default: false) */
  overlay?: boolean;
  /** Close when clicking the overlay (default: true, only applies when overlay=true) */
  closeOnOverlayClick?: boolean;
  /** Panel content */
  children: React.ReactNode;
  /** Optional header actions (rendered in header, right of title) */
  headerActions?: React.ReactNode;
  /** Optional leading element (rendered in header, left of title — useful for collapse icons) */
  headerLeading?: React.ReactNode;
  /** Called when the header bar is clicked (useful for collapse-on-click) */
  onHeaderClick?: () => void;
  /** Custom style overrides for the header bar */
  headerStyle?: React.CSSProperties;
  /** Custom className */
  className?: string;
  /** Custom style overrides for the panel container */
  style?: React.CSSProperties;
}

// ============================================================================
// Unique animation ID
// ============================================================================

let sidePanelInstanceCount = 0;

// ============================================================================
// Component
// ============================================================================

export const SidePanel = memo(function SidePanel({
  open,
  onClose,
  title,
  width = 380,
  position = "right",
  showCloseButton = true,
  closeOnEscape = true,
  overlay = false,
  closeOnOverlayClick = true,
  children,
  headerActions,
  headerLeading,
  onHeaderClick,
  headerStyle: headerStyleProp,
  className = "",
  style: styleProp,
}: SidePanelProps): React.ReactElement | null {
  const { tokens, theme } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  const panelRef = useRef<HTMLDivElement>(null);
  const [animState, setAnimState] = useState<
    "closed" | "opening" | "open" | "closing"
  >("closed");
  const _instanceId = useRef(++sidePanelInstanceCount);

  const { isMobile } = useBreakpoint();
  const resolvedWidth = isMobile
    ? "100%"
    : typeof width === "number"
      ? `${width}px`
      : width;

  // Animate open/close
  useEffect(() => {
    if (open) {
      setAnimState("opening");
      const timer = setTimeout(() => setAnimState("open"), 30);
      return () => clearTimeout(timer);
    } else {
      if (animState === "open" || animState === "opening") {
        setAnimState("closing");
        const timer = setTimeout(() => setAnimState("closed"), 550);
        return () => clearTimeout(timer);
      }
    }
  }, [open]);

  // Escape key handler
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (closeOnEscape && e.key === "Escape") {
        onClose();
      }
    },
    [closeOnEscape, onClose],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [open, handleKeyDown]);

  // Don't render at all when fully closed
  if (animState === "closed") return null;

  const isVisible = animState === "open";
  const slideFrom =
    position === "right" ? "translateX(100%)" : "translateX(-100%)";

  return (
    <>
      {/* Optional overlay */}
      {overlay && (
        <div
          className="zendir-sidepanel-overlay"
          onClick={closeOnOverlayClick ? onClose : undefined}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: isTransparentTheme
              ? `${tokens.colors.background.base}4D`
              : `${tokens.colors.background.base}66`,
            backdropFilter: isTransparentTheme ? "blur(2px)" : undefined,
            WebkitBackdropFilter: isTransparentTheme ? "blur(2px)" : undefined,
            zIndex: 999,
            opacity: isVisible ? 1 : 0,
            transition: "opacity 0.3s ease",
            pointerEvents: isVisible ? "auto" : "none",
          }}
        />
      )}

      {/* Panel */}
      <div
        ref={panelRef}
        className={classNames("zendir-sidepanel", className)}
        style={{
          width: isVisible ? resolvedWidth : "0px",
          minWidth: isVisible ? resolvedWidth : "0px",
          maxWidth: resolvedWidth,
          height: "100%",
          overflow: "hidden",
          transition:
            "width 0.6s cubic-bezier(0.2, 0.8, 0.2, 1), min-width 0.6s cubic-bezier(0.2, 0.8, 0.2, 1)",
          flexShrink: 0,
          position: "relative",
          zIndex: overlay ? 1000 : "auto",
          ...(overlay && { position: "fixed", top: 0, [position]: 0 }),
          ...styleProp,
        }}
      >
        <div
          style={{
            width: resolvedWidth,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            backgroundColor: isTransparentTheme
              ? "rgba(15, 15, 35, 0.85)"
              : tokens.colors.background.surface,
            ...(isTransparentTheme && {
              backdropFilter: "blur(16px)",
              WebkitBackdropFilter: "blur(16px)",
            }),
            borderLeft:
              position === "right" ? tokens.borders.divider : undefined,
            borderRight:
              position === "left" ? tokens.borders.divider : undefined,
            transform: isVisible ? "translateX(0)" : slideFrom,
            transition: "transform 0.6s cubic-bezier(0.2, 0.8, 0.2, 1)",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          {(title || showCloseButton || headerActions || headerLeading) && (
            <div
              onClick={onHeaderClick}
              role={onHeaderClick ? "button" : undefined}
              tabIndex={onHeaderClick ? 0 : undefined}
              onKeyDown={
                onHeaderClick
                  ? (e: React.KeyboardEvent) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onHeaderClick();
                      }
                    }
                  : undefined
              }
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: `${tokens.spacing.md} ${tokens.spacing.md}`,
                borderBottom: tokens.borders.divider,
                flexShrink: 0,
                gap: tokens.spacing.sm,
                ...(onHeaderClick && { cursor: "pointer" }),
                ...headerStyleProp,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: tokens.spacing.sm,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {headerLeading}
                {title && (
                  <h3
                    style={{
                      margin: 0,
                      fontSize: tokens.typography.fontSize.base,
                      fontWeight: tokens.typography.fontWeight.semibold,
                      color: tokens.colors.text.primary,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {title}
                  </h3>
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: tokens.spacing.xs,
                  flexShrink: 0,
                }}
              >
                {headerActions}
                {showCloseButton && (
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close panel"
                    style={{
                      padding: tokens.spacing.sm,
                      minWidth: 44,
                      minHeight: 44,
                      backgroundColor: "transparent",
                      border: "none",
                      borderRadius: tokens.borderRadius.sm,
                      cursor: "pointer",
                      color:
                        tokens.colors.text.muted ?? tokens.colors.text.tertiary,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: `color 0.15s ease, background-color 0.15s ease`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = tokens.colors.text.primary;
                      e.currentTarget.style.backgroundColor =
                        tokens.colors.background.base;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color =
                        tokens.colors.text.muted ?? tokens.colors.text.tertiary;
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Content */}
          <div
            style={{
              flex: 1,
              overflow: "auto",
              minHeight: 0,
            }}
          >
            {children}
          </div>
        </div>
      </div>
    </>
  );
});

export default SidePanel;
